import * as yup from "yup";
import { kanaAlpNum } from "../utility/ValidationUtilty";
import { ConstMessage } from "../config/const";
import companiesQuery from "../firestore/Companies.query";
import { OptionContract } from "../model/OptionContract";
import ExcelJS from "exceljs";

export type User = {
  id: string;
  tenantId: string;
  lastName?: string;
  firstName?: string;
  lastNameKana?: string;
  firstNameKana?: string;
  birthDate?: Date;
  sex?: number;
  phoneNumber?: string;
  faxNumber?: string;
  iconURL?: string;
  familyEmail?: string;
  tag?: string[];
  groupCount?: number;
  devicesCount?: number;
  createAt?: Date;
  updateAt: Date;
  admin?: boolean;
  deleteFlg?: boolean;
  accountSuspensionFlg: boolean;
  note?: string;
  firstLoginDate?: Date;
  deleteAt?: Date | null;
  zipCode?: string;
  address?: string;
  searchKey1?: string;
  searchKey2?: string;
  searchKey3?: string;
  displayId?: string;
  pointAssignmentAuthority?: boolean | null;
  loginDone?: boolean,
}

export const UserItemMaping = (data: any | undefined): User | UserData => {
  // familyEmailが配列ならjoin、それ以外はそのまま文字列化
  if (Array.isArray(data?.familyEmail)) {
    data.familyEmail = data.familyEmail.join(",");
  } else if (typeof data?.familyEmail === "string") {
    data.familyEmail = data.familyEmail;
  } else {
    data.familyEmail = "";
  }
  data.birthDate = data?.birthDate?.seconds != null
      ? new Date(data?.birthDate?.seconds * 1000)
    : data?.birthDate;
  data.createAt = new Date(data?.createAt?.seconds * 1000);
  data.updateAt = data?.updateAt !== null
    ? new Date(data?.updateAt.seconds * 1000)
    : new Date();
  data.firstLoginDate = data?.firstLoginDate !== null
    ? new Date(data?.firstLoginDate?.seconds * 1000)
    : data?.firstLoginDate;
  return data;
}

/**
 * DBの取得結果を UserData 型に変換
 * @param data 
 * @returns 
 */
export const UserItemMapingFromDb = (data: any | undefined): User | UserData => {
  data.familyEmail = data?.familyEmail !== undefined
    ? data?.familyEmail.join(",")
    : "";

  data.birthDate = data?.birthDate !== null
    ? new Date(data?.birthDate) : undefined;

  data.createAt = new Date(data?.createAt);

  data.updateAt = data?.updateAt !== null
    ? new Date(data?.updateAt) : undefined;

  data.firstLoginDate = data?.firstLoginDate !== null
    ? new Date(data?.firstLoginDate) : undefined;

  return data;
}

export const initialUserInfo: User = {
  id: "",
  tenantId: "",
  lastName: "",
  firstName: "",
  lastNameKana: "",
  firstNameKana: "",
  birthDate: undefined,
  sex: 0,
  phoneNumber: "",
  faxNumber: "",
  iconURL: "",
  familyEmail: "",
  tag: [],
  groupCount: 0,
  devicesCount: 0,
  createAt: new Date(),
  updateAt: new Date(),
  admin: false,
  deleteFlg: false,
  accountSuspensionFlg: false,
  note: "",
  firstLoginDate: undefined,
  deleteAt: null,
  zipCode: "",
  address: "",
  searchKey1: "",
  searchKey2: "",
  searchKey3: "",
  pointAssignmentAuthority: null,
}

export type UserData = User & {
  groups: GroupsSetting[],
  salt?: string | null,
  encryptedPasscode?: string | null,
  decryptedPasscode?: string | null,
  password?: string | null,
  passcode?: string | null,
  iv?: Blob | null,
  possessedPointMap?: possessedPointMap[],
  expirationPointMap?: expirationPointMap[],
};

export const initialUserDataInfo: UserData = {
  id: "",
  tenantId: "",
  lastName: "",
  firstName: "",
  lastNameKana: "",
  firstNameKana: "",
  birthDate: undefined,
  sex: 0,
  phoneNumber: "",
  faxNumber: "",
  iconURL: "",
  familyEmail: "",
  tag: [],
  groupCount: 0,
  devicesCount: 0,
  createAt: new Date(),
  updateAt: new Date(),
  admin: false,
  salt: "",
  password: "",
  passcode: "",
  encryptedPasscode: "",
  decryptedPasscode: "",
  iv: new Blob(),
  deleteFlg: false,
  accountSuspensionFlg: false,
  note: "",
  groups: [],
  firstLoginDate: undefined,
  deleteAt: null,
  zipCode: "",
  address: "",
  searchKey1: "",
  searchKey2: "",
  searchKey3: "",
  pointAssignmentAuthority: null,
  possessedPointMap: undefined,
  expirationPointMap: undefined,
}

export enum ColorList {
  "blue" = 0,
  "green" = 1,
  "red" = 2,
  "yellow" = 3,
  "indigo" = 4,
  "purple" = 5,
  "pink" = 6,
  "gray" = 7
}

export type possessedPointMap = {
  id?: string,
  name?: string,
  point?: number,
};

export const possessedPointMap = (): possessedPointMap[] => {
  return[
    {
      id: "",
      name: "",
      point: undefined,
    }
  ]
};

export type expirationPointMap = {
  id?: string,
  name?: string,
  point?: number,
  expirationDate?: Date,
}

export type GroupsSetting = {
  id: string;
  name: string;
  color: "blue" | "green" | "red" | "yellow" | "indigo" | "purple" | "pink" | "gray";
  disabled?: boolean | null | undefined;
  adminFlg?: boolean | undefined;
}

export const userSchema = (tenatId: string) => yup.object({
  lastName: yup.string().required(),
  lastNameKana: kanaAlpNum,
  birthDate: yup.string().test((value, testContext) => {
    const birthDateValue = testContext.parent.birthDate;
    if (birthDateValue === 'Invalid Date' || birthDateValue === '' || birthDateValue === undefined) {
      return true;
    } else if (Number.isNaN(new Date(birthDateValue).getTime())) {
      return testContext.createError({
        message: ConstMessage.ERR_BIRTH_DATE_INVALID_FORMAT
      });
    }
    return true;
  }),
  sex: yup.number().nullable().transform((value, originalValue) => 
    String(originalValue).trim() === '' ? null : value
  ),
  admin: yup.boolean().required(),
  deleteFlg: yup.boolean().required(),
  pointAssignmentAuthority: yup
  .boolean()
  .nullable()
  .test("required-if-admin", ConstMessage.SELECTION_REQUIRED, async function (value) {  
    const { admin } = this.parent;
    const options = await companiesQuery.getOptionContractList(tenatId);
    let option: OptionContract | undefined = options.find((option) => option.optionId === 'point');
    if (admin === true && value === null && option?.status === true) {
      return this.createError({ message: ConstMessage.SELECTION_REQUIRED });
    }
    return true;
  }),

  zipCode: yup.string().matches(/^\d{3}-\d{4}$|^$/, ConstMessage.ERR_ZIP_CODE_INVALID_FORMAT),
  searchKey1: yup.string().test( async (value, testContext)=>{
    const searchKey1Value = testContext.parent.searchKey1;
    const searchKeys = await companiesQuery.getSearchKeys(tenatId);
    let searchNameList: string[] = [''];
    if (searchKeys.length > 0) {
      searchKeys.map((item) => {
        const keyValues = item.keyValues;
        if (keyValues.length > 0) {
          keyValues.sort((a, b) => a.sort - b.sort);
          if (item.name === "userSearchKey1") {
            searchNameList = [...keyValues].map(obj => obj.searchName);
            searchNameList.unshift('');
          }
        }
      })
    }
    if (searchNameList.includes(searchKey1Value) === false) {
      return testContext.createError({
        message: ConstMessage.ERR_NOT_REGISTERED_DATA
      })
    }
    return true;
  }),
  searchKey2: yup.string().test( async (value, testContext)=>{
    const searchKey2Value = testContext.parent.searchKey2;
    const searchKeys = await companiesQuery.getSearchKeys(tenatId);
    let searchNameList: string[] = [''];
    if (searchKeys.length > 0) {
      searchKeys.map((item) => {
        const keyValues = item.keyValues;
        if (keyValues.length > 0) {
          keyValues.sort((a, b) => a.sort - b.sort);
          if (item.name === "userSearchKey2") {
            searchNameList = [...keyValues].map(obj => obj.searchName);
            searchNameList.unshift('');
          }
        }
      })
    }

    if (searchNameList.includes(searchKey2Value) === false) {
      return testContext.createError({
        message: ConstMessage.ERR_NOT_REGISTERED_DATA
      })
    }
    return true;
  }),
  searchKey3: yup.string().test( async (value, testContext)=>{
    const searchKey3Value = testContext.parent.searchKey3;
    const searchKeys = await companiesQuery.getSearchKeys(tenatId);
    let searchNameList: string[] = [''];
    if (searchKeys.length > 0) {
      searchKeys.map((item) => {
        const keyValues = item.keyValues;
        if (keyValues.length > 0) {
          keyValues.sort((a, b) => a.sort - b.sort);
          if (item.name === "userSearchKey3") {
            searchNameList = [...keyValues].map(obj => obj.searchName);
            searchNameList.unshift('');
          }
        }
      })
    }
    if (searchNameList.includes(searchKey3Value) === false) {
      return testContext.createError({
        message: ConstMessage.ERR_NOT_REGISTERED_DATA
      })
    }
    return true;
  }),
})

export type Devices = {
  id:string;
  token: string;
  timeStamp: Date;
}

export type AttendingGroups = {
  id: string;
  userId: string;
  tenantId: string;
  addDate: Date; 
  adminFlg?: boolean;
}

export type Password = {
  id: string;
  userId: string;
  password: string;
  createAt: Date;
}

export type Passcode = {
  id: string;
  userId: string;
  encryptedPasscode: string;
  createAt: Date;
}

export type Iv = {
  id: string;
  userId: string;
  iv: Blob;
  createAt: Date;
}

export type ReadMessages = {
  messageId: string;
  userId: string;
  readAt?: Date;
  replyAt?: Date;
  replyButtonName?: string;
  sendDate?: Date;
  eventAt?: Date;
  attendanceButtonName?: string;
  attendanceAt?: Date;
  attendanceFlg?: boolean;
  confirmationCheck?: boolean;
  participationFlg?: boolean;
  answeredAt?: Date;
  answers?: string[];
  answersFlg?: boolean;
}

// 利用者検索用
export type UserSearchData = {
  id: string;
  lastName: string;
  lastNameKana?: string;
};

// 利用者検索初期値
export const UserSearchDefault = (): UserSearchData => {
  return  {
    id: "",
    lastName: "",
    lastNameKana: "",
  };
};

// 返信・出欠一覧検索用
export interface SearchCriteria {
  read?: string;
  status?: string;
  name?: string;
  account?: string;
  replyData?: string;
  attendanceData?: string;
  group?: string[];
}
// 利用者一覧検索用
export type SearchUserList = {
  userId: string;
  sex: string;
  birthDateStart: string;
  birthDateEnd: string;
  name: string;
  zipCode: string;
  address: string;
  login: string;
  role: string;
  appUsage: string;
  searchKey1: string;
  searchKey2: string;
  searchKey3: string;
  topic: [];
}

// 利用者一覧検索初期値
export const UserListSearchDefault = (): SearchUserList => {
  return {
    userId: "",
    sex: "",
    birthDateStart: "",
    birthDateEnd: "",
    name: "",
    zipCode: "",
    address: "",
    login: "",
    role: "",
    appUsage: "",
    searchKey1: "",
    searchKey2: "",
    searchKey3: "",
    topic: [],
  }
}

// アプリ利用
export const APP_USE = {
  AVAILABLE: 0,  // 利用可
  UNUSEABLE: 1,  // 利用不可
  SUSPEND: 2,    // 利用停止
} as const

export type AppUseType = typeof APP_USE[keyof typeof APP_USE];

// UserまたはUserDataのオブジェクトからアプリ利用の判定
export const GetAppUseStateFromUserData = (user: User | UserData) => {
  return GetAppUseState(user.deleteFlg, user.accountSuspensionFlg);
}

// アプリ利用判定
export const GetAppUseState = (deleteFlg: boolean | undefined, accountSuspensionFlg: boolean): AppUseType | undefined => {
  let appUse = undefined;
  
  if (deleteFlg === false && accountSuspensionFlg === false) {
    // 利用可
    appUse = APP_USE.AVAILABLE;
  } else if (deleteFlg === true) {
    // 利用不可
    appUse = APP_USE.UNUSEABLE;
  } else if (accountSuspensionFlg === true) {
    // 利用停止
    appUse = APP_USE.SUSPEND;
  }

  return appUse;
}

export type UserCountList = {
  idRegistered: number;
  usable: number;
  unusable: number;
  suspended: number;
};

export type RowData = {
  lastName: string;
  lastNameKana: string;
  birthDateVal: ExcelJS.CellValue;
  birthday: Date | undefined;
  sexStr: string;
  sex: number | undefined;
  phoneNumber: string;
  faxNumber: string;
  familyEmail: string;
  admin: boolean | undefined;
  deleteFlg: boolean | undefined;
  accountSuspensionFlg: boolean | undefined;
  zipCode: string;
  address: string;
  searchKey1: string;
  searchKey2: string;
  searchKey3: string;
  note?: string;
  groupNameList: {
    id?: string;
    name: string;
    col: string;
    color?: string;
  }[];
};

export const formatRowDataToUserData = (rowData: RowData, id: string, tenantId: string): UserData => {
  const user: UserData = {
    ...initialUserInfo,
    id,
    tenantId,
    lastName: rowData.lastName,
    firstName: '',
    lastNameKana: rowData.lastNameKana,
    firstNameKana: '',
    birthDate: rowData.birthday,
    sex: rowData.sex,
    phoneNumber: rowData.phoneNumber,
    faxNumber: rowData.faxNumber,
    familyEmail: Array.isArray(rowData.familyEmail)
      ? rowData.familyEmail[0] ?? undefined
      : rowData.familyEmail,
    admin: rowData.admin ?? false,
    deleteFlg: rowData.deleteFlg ?? false,
    accountSuspensionFlg: rowData.accountSuspensionFlg ?? false,
    zipCode: rowData.zipCode,
    address: rowData.address,
    searchKey1: rowData.searchKey1,
    searchKey2: rowData.searchKey2,
    searchKey3: rowData.searchKey3,
    note: rowData.note,
    groups: rowData.groupNameList.map((g) => ({
      id: g.id ?? g.name,
      name: g.name,
      col: g.col,
      color: (g.color as GroupsSetting["color"]) ?? "blue",
    })),
  };

  return user;
};

export const sanitizeUsers = (users: UserData[]): UserData[] => {
  return users.map(user => {
    const sanitized: any = {
      id: user.id,
      tenantId: user.tenantId,
      lastName: user.lastName ?? null,
      firstName: user.firstName ?? null,
      lastNameKana: user.lastNameKana ?? null,
      firstNameKana: user.firstNameKana ?? null,
      birthDate: user.birthDate ?? null,
      sex: user.sex ?? null,
      phoneNumber: user.phoneNumber ?? null,
      faxNumber: user.faxNumber ?? null,
      iconURL: user.iconURL ?? null,
      familyEmail: Array.isArray(user.familyEmail)
        ? user.familyEmail.filter(Boolean).join(",")
        : user.familyEmail ?? null,
      groupCount: user.groupCount ?? 0,
      devicesCount: user.devicesCount ?? 0,
      updateAt: user.updateAt ?? new Date(),
      admin: user.admin ?? false,
      deleteFlg: user.deleteFlg ?? false,
      accountSuspensionFlg: user.accountSuspensionFlg ?? false,
      groups: user.groups ?? [],
      note: user.note ?? null,
      zipCode: user.zipCode ?? null,
      address: user.address ?? null,
      searchKey1: user.searchKey1 ?? null,
      searchKey2: user.searchKey2 ?? null,
      searchKey3: user.searchKey3 ?? null,
    };
    return sanitized as UserData;
  });
};